import React, { memo } from 'react';

/**
 * 表示するページ番号・省略記号の配列を返す
 * - 先頭(1)と末尾(n)は常に表示
 * - アンカー: page≤2→2, page≥n-1→n-1, それ以外→current
 * - 隣接しない箇所に '...' を挿入
 */
function getPageNumbers(page, totalPages) {
  // 先頭(1)と末尾(totalPages)は常に表示する
  const show = new Set([1, totalPages]);

  // 端に近いページではアンカーを端寄りに固定し、省略記号が不要になるケースを防ぐ
  if (page <= 2) show.add(2);
  else if (page >= totalPages - 1) show.add(totalPages - 1);
  else show.add(page);

  const sorted = Array.from(show)
    .filter(p => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    // 隣接しないページ番号の間に '...' を挿入する
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
    result.push(sorted[i]);
  }
  return result;
}

/**
 * ページャーコンポーネント
 *
 * 前ページ・次ページへの移動とページ番号ボタンによる直接ジャンプを提供する。
 * totalPages が 0 以下の場合は何も表示しない。
 *
 * @param {Object} props
 * @param {number} props.page - 現在のページ番号（1始まり）
 * @param {number} props.totalPages - 総ページ数
 * @param {Function} props.onPageChange - ページ変更コールバック（引数: number）
 */
const Pagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 0) return null;

  return (
    <div className="dc-pagination">
      <div className="dc-pagination__controls">
        <button
          className="dc-pagination__nav-btn"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          前へ
        </button>
        <div className="dc-pagination__pages">
          {getPageNumbers(page, totalPages).map((p, i) =>
            p === '...'
              ? <span key={`ellipsis-${i}`} className="dc-pagination__ellipsis">…</span>
              : <button
                  key={p}
                  className={`dc-pagination__page-btn${p === page ? ' dc-pagination__page-btn--active' : ''}`}
                  onClick={() => onPageChange(p)}
                  disabled={p === page}
                >
                  {p}
                </button>
          )}
        </div>
        <button
          className="dc-pagination__nav-btn"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
        >
          次へ
        </button>
      </div>
    </div>
  );
};

export default memo(Pagination);
